import {
	ChangeDetectionStrategy,
	Component,
	input,
	output,
} from '@angular/core';
import type {
	OperatorReservation,
	ReservationStatus,
} from '@pressteaze/domain';

@Component({
	selector: 'app-operator-reservation-card',
	templateUrl: './reservation-card.component.html',
	styleUrl: './reservation-card.component.css',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OperatorReservationCardComponent {
	readonly reservation = input.required<OperatorReservation>();
	readonly timezone = input.required<string>();
	readonly referenceServiceDate = input<string | null>(null);
	readonly expanded = input(false);
	readonly saving = input(false);
	readonly toggled = output<void>();
	readonly editRequested = output<void>();
	readonly transitionRequested = output<ReservationStatus>();

	toggleFromKeyboard(event: Event): void {
		if (event.target !== event.currentTarget) return;
		event.preventDefault();
		this.toggled.emit();
	}

	formatTime(value: string): string {
		return new Intl.DateTimeFormat('el-GR', {
			timeZone: this.timezone(),
			hour: '2-digit',
			minute: '2-digit',
			hourCycle: 'h23',
		}).format(new Date(value));
	}

	showsServiceDate(): boolean {
		const referenceDate = this.referenceServiceDate();
		return (
			referenceDate !== null && this.reservation().serviceDate !== referenceDate
		);
	}

	formatServiceDate(value: string): string {
		return new Intl.DateTimeFormat('el-GR', {
			day: 'numeric',
			month: 'short',
		})
			.format(new Date(value + 'T12:00:00Z'))
			.replace('.', '');
	}

	formatTimeRange(): string {
		return (
			this.formatTime(this.reservation().startsAt) +
			'–' +
			this.formatTime(this.reservation().endsAt)
		);
	}

	statusActions(
		status: ReservationStatus,
	): readonly { status: ReservationStatus; icon: string }[] {
		switch (status) {
			case 'pending':
				return [
					{ status: 'booked', icon: 'check' },
					{ status: 'cancelled', icon: 'ban' },
				];
			case 'booked':
				return [
					{ status: 'arrived', icon: 'user-round-check' },
					{ status: 'no_show', icon: 'user-round-x' },
					{ status: 'cancelled', icon: 'ban' },
				];
			case 'arrived':
				return [{ status: 'completed', icon: 'check-double-solid-full' }];
			default:
				return [];
		}
	}

	statusLabel(status: ReservationStatus): string {
		return {
			pending: 'Σε αναμονή',
			booked: 'Επιβεβαιωμένη',
			arrived: 'Έφτασε',
			completed: 'Ολοκληρώθηκε',
			cancelled: 'Ακυρώθηκε',
			no_show: 'Δεν ήρθε',
			expired: 'Έληξε χωρίς επιβεβαίωση',
		}[status];
	}

	sourceLabel(source: OperatorReservation['source']): string {
		return { online: 'Online', phone: 'Τηλεφωνική', walk_in: 'Walk-in' }[source];
	}
}
